import type { Client360Data } from "@/mocks/client360";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

function TrendBadge({ trend, pct }: { trend: "рост" | "падение" | "стабильно"; pct: number }) {
  if (trend === "рост") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] text-[10px] font-medium">
        <i className="ri-arrow-up-line text-[10px]"></i>
        Рост +{pct}%
      </span>
    );
  }
  if (trend === "падение") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FFEDD5] text-[#DC2626] text-[10px] font-medium">
        <i className="ri-arrow-down-line text-[10px]"></i>
        Падение -{pct}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FDF2B5] text-[#666666] text-[10px] font-medium">
      <i className="ri-subtract-line text-[10px]"></i>
      Стабильно
    </span>
  );
}

function GaugeBar({ value, label, colorClass }: { value: number; label: string; colorClass: string }) {
  const getLevelColor = () => {
    if (colorClass === "green") return "bg-[#2D6A4F]";
    if (colorClass === "red") return "bg-[#DC2626]";
    return "bg-[#D5CA8A]";
  };

  const getLevelLabel = () => {
    if (colorClass === "green") return "text-[#2D6A4F]";
    if (colorClass === "red") return "text-[#DC2626]";
    return "text-[#666666]";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-[#666666]">{label}</span>
        <span className={`text-[11px] font-bold ${getLevelLabel()}`}>{value}%</span>
      </div>
      <div className="w-full h-[6px] bg-[#FDF2B5] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${getLevelColor()}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  );
}

export default function AiInsights({ data }: { data: Client360Data }) {
  const priorityColor = (p: string) => {
    if (p === "Высокий") return "bg-[#FFEDD5] text-[#DC2626]";
    if (p === "Средний") return "bg-[#FEF3C7] text-[#2D2D2D]";
    return "bg-[#FDF2B5] text-[#666666]";
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "Новый": return "bg-[#E8F4FD] text-[#1E6FAD]";
      case "Активный": return "bg-[#D4EDDA] text-[#2D6A4F]";
      case "Уснувший": return "bg-[#FDF2B5] text-[#8B7E3A]";
      case "Потерянный": return "bg-[#FFEDD5] text-[#DC2626]";
      case "VIP": return "bg-[#FEF3C7] text-[#8B6914]";
      case "Должник": return "bg-[#FFEDD5] text-[#DC2626]";
      default: return "bg-[#D4EDDA] text-[#2D6A4F]";
    }
  };

  return (
    <div className="space-y-3">
      {/* Row A: Purchase Analytics + Churn & Repeat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Purchase Analytics */}
        <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
          <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
            <span className="text-[11px] font-semibold text-[#2D2D2D]">Аналитика покупок</span>
            <TrendBadge trend={data.purchaseTrend} pct={data.purchaseTrendPct} />
          </div>
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#FDF2B5]/30 rounded p-2">
                <p className="text-[9px] text-[#666666]">Дата последней покупки</p>
                <p className="text-[11px] font-semibold text-[#2D2D2D] mt-0.5">{data.lastPurchaseDate}</p>
              </div>
              <div className="bg-[#FDF2B5]/30 rounded p-2">
                <p className="text-[9px] text-[#666666]">Частота закупок</p>
                <p className="text-[11px] font-semibold text-[#2D2D2D] mt-0.5">{data.purchaseFrequency}</p>
              </div>
              <div className="bg-[#FDF2B5]/30 rounded p-2">
                <p className="text-[9px] text-[#666666]">Средний чек</p>
                <p className="text-[11px] font-semibold text-[#2D2D2D] mt-0.5">{formatMoney(data.avgCheck)}</p>
              </div>
              <div className="bg-[#FDF2B5]/30 rounded p-2">
                <p className="text-[9px] text-[#666666]">Продажи за год</p>
                <p className="text-[11px] font-semibold text-[#2D2D2D] mt-0.5">{formatMoney(data.sales12m)}</p>
              </div>
            </div>

            <GaugeBar
              value={data.repeatSalePotential}
              label="Потенциал повторной продажи"
              colorClass={data.repeatSalePotential > 50 ? "green" : data.repeatSalePotential > 25 ? "yellow" : "red"}
            />

            {/* ReorderScore Table */}
            <div className="border border-[#D5CA8A]/40 rounded bg-[#FDF2B5]/10 overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_60px_50px_50px] gap-1 px-2 py-1 bg-[#FDF2B5]/15 border-b border-[#D5CA8A]/20">
                <span className="text-[8px] font-semibold text-[#666666] truncate">Товар</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Интервал</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Score</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Статус</span>
              </div>

              {/* Product rows */}
              {data.reorderScores.map((r) => {
                const statusConfig = {
                  рано: { color: "text-[#666666]", bg: "bg-[#FDF2B5]/50", label: "Рано" },
                  пик: { color: "text-[#2D6A4F]", bg: "bg-[#D4EDDA]", label: "Пик" },
                  просрочен: { color: "text-[#8B6914]", bg: "bg-[#FEF3C7]", label: "Просрочен" },
                  потерян: { color: "text-[#DC2626]", bg: "bg-[#FFEDD5]", label: "Потерян" },
                };
                const cfg = statusConfig[r.status];
                return (
                  <div key={r.product} className="grid grid-cols-[1fr_60px_50px_50px] gap-1 px-2 py-1 border-b border-[#D5CA8A]/10 last:border-0 items-center">
                    <span className="text-[9px] text-[#2D2D2D] font-medium truncate block" title={r.product}>{r.product}</span>
                    <span className="text-[8px] text-[#999999] text-center">{r.daysSinceLastPurchase} / {r.avgIntervalDays} дн.</span>
                    <span className="text-[9px] font-bold text-center text-[#2D2D2D]">{r.score}%</span>
                    <span className={`text-[8px] font-semibold text-center px-1 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                  </div>
                );
              })}

              <div className="px-2 py-1 flex items-center gap-2 border-t border-[#D5CA8A]/30">
                <span className="w-2 h-2 rounded-full bg-[#666666]" />
                <span className="text-[8px] text-[#666666]">&lt;70% рано</span>
                <span className="w-2 h-2 rounded-full bg-[#2D6A4F] ml-1" />
                <span className="text-[8px] text-[#666666]">70–130% пик</span>
                <span className="w-2 h-2 rounded-full bg-[#D5CA8A] ml-1" />
                <span className="text-[8px] text-[#666666]">130–180% просрочен</span>
                <span className="w-2 h-2 rounded-full bg-[#DC2626] ml-1" />
                <span className="text-[8px] text-[#666666]">&gt;180% потерян</span>
              </div>
            </div>
          </div>
        </div>

        {/* Churn Risk + Repeat Potential */}
        <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
          <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
            <span className="text-[11px] font-semibold text-[#2D2D2D]">Риски и потенциал</span>
          </div>
          <div className="p-3 space-y-3">
            <GaugeBar
              value={data.churnRisk}
              label={`Риск ухода клиента — ${data.churnRiskLevel}`}
              colorClass={data.churnRisk > 60 ? "red" : data.churnRisk > 30 ? "yellow" : "green"}
            />

            {/* Churn Risk Factor Breakdown — compact 6-column table */}
            <div className="border border-[#D5CA8A]/40 rounded bg-[#FDF2B5]/10 overflow-hidden">
              <div className="px-2 py-1 bg-[#FDF2B5]/30 border-b border-[#D5CA8A]/30">
                <span className="text-[9px] font-semibold text-[#666666] uppercase tracking-wide">Формула: Σ (Вес × Оценка)</span>
              </div>

              {/* Table header */}
              <div className="grid grid-cols-[1fr_1fr_50px_50px_50px] gap-1 px-2 py-1 bg-[#FDF2B5]/15 border-b border-[#D5CA8A]/20">
                <span className="text-[8px] font-semibold text-[#666666] truncate">Фактор</span>
                <span className="text-[8px] font-semibold text-[#666666] truncate">Что показывает</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Вес</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Оценка</span>
                <span className="text-[8px] font-semibold text-[#666666] text-center">Вклад</span>
              </div>

              {/* Factor rows */}
              {data.churnRiskFactors.map((f) => {
                const scoreColor = f.score > 60 ? "text-[#DC2626]" : f.score > 30 ? "text-[#8B6914]" : "text-[#2D6A4F]";
                return (
                  <div key={f.name} className="grid grid-cols-[1fr_1fr_50px_50px_50px] gap-1 px-2 py-1 border-b border-[#D5CA8A]/10 last:border-0 items-center">
                    <span className="text-[9px] text-[#2D2D2D] font-medium truncate" title={f.name}>{f.name}</span>
                    <span className="text-[9px] text-[#666666] truncate" title={f.description}>{f.description}</span>
                    <span className="text-[9px] font-semibold text-[#2D2D2D] text-center">{Math.round(f.weight * 100)}%</span>
                    <span className={`text-[9px] font-bold text-center ${scoreColor}`}>{f.score}</span>
                    <span className="text-[9px] font-bold text-[#2D2D2D] text-center">{f.contribution.toFixed(1)}%</span>
                  </div>
                );
              })}

              <div className="px-2 py-1.5 bg-[#FDF2B5]/20 border-t border-[#D5CA8A]/30 flex items-center justify-between">
                <span className="text-[9px] font-semibold text-[#2D2D2D]">
                  Итог: 0.30×Recency + 0.20×FreqTrend + 0.20×MoneyTrend + 0.15×DebtRisk + 0.15×Engagement
                </span>
                <span className="text-[11px] font-bold text-[#2D6A4F]">{data.churnRisk}%</span>
              </div>
              <div className="px-2 py-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" />
                <span className="text-[8px] text-[#666666]">0–30% низкий</span>
                <span className="w-2 h-2 rounded-full bg-[#D5CA8A] ml-1" />
                <span className="text-[8px] text-[#666666]">30–60% средний</span>
                <span className="w-2 h-2 rounded-full bg-[#DC2626] ml-1" />
                <span className="text-[8px] text-[#666666]">60–100% высокий</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row B: AI Recommendations + Cross-sell */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* AI Recommendations */}
        <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
          <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-[#2D2D2D]">Рекомендации AI</span>
              <span className="w-4 h-4 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                <i className="ri-sparkling-2-line text-[10px] text-white"></i>
              </span>
            </div>
            <span className="text-[9px] text-[#666666]">{data.aiRecommendations.length} рекомендаций</span>
          </div>
          <div className="p-3 space-y-2">
            {data.aiRecommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded bg-[#FDF2B5]/20 border border-[#D5CA8A]/20 hover:bg-[#FDF2B5]/40 transition-colors cursor-pointer">
                <div className="flex items-center gap-1 shrink-0 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${rec.priority === "Высокий" ? "bg-[#DC2626]" : rec.priority === "Средний" ? "bg-[#D5CA8A]" : "bg-[#999999]"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-[10px] text-[#2D2D2D] font-medium">{rec.title}</p>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium whitespace-nowrap ${priorityColor(rec.priority)}`}>
                      {rec.priority}
                    </span>
                  </div>
                  <p className="text-[9px] text-[#666666] leading-relaxed">{rec.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cross-sell Products */}
        <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
          <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
            <span className="text-[11px] font-semibold text-[#2D2D2D]">Товары для допродажи</span>
            <span className="text-[9px] text-[#666666]">{data.crossSellProducts.length} позиций</span>
          </div>
          <div className="p-3">
            <div className="flex items-center text-[10px] text-[#666666] px-1 py-0.5 bg-[#FDF2B5] rounded mb-2">
              <span className="flex-1">Группа / Товар</span>
              <span className="w-[90px] text-right">Потенциал, ₽</span>
            </div>
            {data.crossSellProducts.map((item, i) => (
              <div key={i} className="py-2 border-b border-[#D5CA8A]/20 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="px-1.5 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] text-[9px] whitespace-nowrap">{item.group}</span>
                      <span className="text-[10px] text-[#2D2D2D] font-medium truncate">{item.product}</span>
                    </div>
                    <p className="text-[9px] text-[#666666] leading-relaxed">{item.reason}</p>
                  </div>
                  <span className="text-[10px] font-semibold text-[#2D6A4F] whitespace-nowrap">{item.potentialAmount.toLocaleString("ru-RU")}</span>
                </div>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t border-[#D5CA8A] flex items-center justify-between">
              <span className="text-[10px] font-semibold text-[#2D2D2D]">Общий потенциал допродаж</span>
              <span className="text-[11px] font-bold text-[#2D6A4F]">
                {formatMoney(data.crossSellProducts.reduce((s, p) => s + p.potentialAmount, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}